import { type AxiosInstance, type AxiosError, HttpStatusCode } from "axios";
import useSWR from "swr";
import type { KeyedMutator } from "swr";
import type {
  GetPropertySummaryEntriesAPIRequest,
  GetPropertySummaryListAPIResponse,
} from "@/features/propertySummary/types";
import { SendToTrackingService } from "@/api/handlers/errors";

interface IResponse {
  data: GetPropertySummaryListAPIResponse | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<GetPropertySummaryListAPIResponse>;
  error: AxiosError | undefined;
}

/**
 * 物件概要書の一覧を取得する
 *
 * @param {AxiosInstance} apiClient - HTTPリクエストを行うためのAxiosのインスタンス
 * @param {GetPropertySummaryEntriesAPIRequest | undefined} requests - 検索フィルタリングを行う場合に仕様する（未実装）
 * @returns {IResponse} APIのレスポンスデータ、ローディング状態、ミューテーション関数を含むオブジェクト。
 */
const GetPropertySummaryListAPI = (
  apiClient: AxiosInstance,
  requests: GetPropertySummaryEntriesAPIRequest | undefined
): IResponse => {
  const fetcher = async (
    url: string,
    params: GetPropertySummaryEntriesAPIRequest
  ): Promise<GetPropertySummaryListAPIResponse> => {
    let urlWithParams: string;
    if (params.keyword === "") {
      urlWithParams = url;
    } else {
      urlWithParams =
        url +
        "?" +
        new URLSearchParams([["keyword", params.keyword]]).toString();
    }

    const { data } = await apiClient.get<GetPropertySummaryListAPIResponse>(
      urlWithParams
    );
    return data;
  };

  const { data, isLoading, mutate, error } = useSWR<
    GetPropertySummaryListAPIResponse,
    AxiosError
  >(
    ["/property-summary/", requests],
    async ([url, params]: [string, GetPropertySummaryEntriesAPIRequest]) =>
      await fetcher(url, params),
    {
      revalidateOnFocus: true,
      refreshInterval: 1000 * 30,
    }
  );

  if (
    error &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    SendToTrackingService(error);
    return { data: undefined, isLoading, mutate, error };
  }

  return { data, isLoading, mutate, error };
};

export default GetPropertySummaryListAPI;
